/**
 * modalStack — the open <Modal> dialogs, in the order they opened. The last
 * one is the TOP, and only the top acts on the keyboard.
 *
 * WHY: every open <Modal> listens for Escape and Tab on the document, in the
 * capture phase. With two open, both listeners saw every key. The real case:
 * the Progress walkthrough sends a tutor request as it opens, a 426 / 403 makes
 * the COPPA interceptor open the age gate or the parent-invite over it, and one
 * Escape in the gate closed the gate AND the walkthrough. Tab ran both focus
 * traps, so a pending dialog underneath (every control disabled while its own
 * request is out) pulled focus out of the gate onto itself. stopPropagation()
 * cannot separate them: it stops listeners on other nodes, not the others on
 * the same document. stopImmediatePropagation() would let the BOTTOM dialog
 * win, because it registered first.
 *
 * TOP = OPENED LAST, the rule the browser's top layer uses for
 * <dialog>.showModal(). Not DOM order: <AgeConsentGate> and <UpgradeDialogHost>
 * render before <main> in App.js, so a gate sits EARLIER in the DOM than the
 * page dialog it opens over. Paint order has to agree, in two layers:
 *   - the gates' overlays use the --z-system-overlay tier (styles/tokens.css),
 *     above every page dialog and full-screen shell, which orders the usual
 *     case: a gate opening over the page dialog whose request raised it; and
 *   - a <Modal> that opens while another open one paints at or above it lifts
 *     its own scrim one above (Modal.js). That covers two dialogs on ONE tier
 *     (CheckoutFlow, inside the upgrade dialog, asks for the date of birth,
 *     and the upgrade host renders after the gate host) and a page dialog that
 *     opens by itself over a gate (PrepPath's countdown auto-submit mounts the
 *     score summary): whichever opened last is on top, for keys and paint.
 *
 * A <Modal> nested in another's content (a discard confirm inside an editor)
 * is on top because it opens later, from an event. One mounted in the SAME
 * commit as its parent would register first, since React runs a child's
 * effects before its parent's, and the parent would also take initial focus
 * and capture the child's control as its opener. No consumer mounts nested
 * dialogs together.
 *
 * useRestoreFocus reads the top too: when a dialog closes and focus cannot go
 * back to its opener, the dialog still open takes it back (focusResume: onto
 * the control the user was last on there, else its initial target) rather than
 * leave it on the page behind. "Still open" means still in the document: when
 * a nested pair closes in one commit (Discard in that editor), React removes
 * both from the DOM, then runs the parent's cleanups first, while the child is
 * still registered. It must not count as open.
 *
 * An entry is { isOpen(), contains(node), focusInitial(), focusResume(),
 * zIndex() }, supplied by <Modal>. focusInitial() puts focus where the dialog
 * puts it on open: for focus that arrives from outside (a focus guard of a
 * dialog under it hands it up). focusResume() gives it back to a user who was
 * in the dialog (useRestoreFocus).
 */
const stack = [];

/** Put `entry` on top. Returns the function that takes it off again. */
export function pushModal(entry) {
  stack.push(entry);
  return () => {
    const i = stack.lastIndexOf(entry);
    if (i !== -1) stack.splice(i, 1);
  };
}

/** The open dialog on top, or null when none is open. */
export function topModal() {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    if (stack[i].isOpen()) return stack[i];
  }
  return null;
}

/** Every open dialog, bottom to top. */
export function openModals() {
  return stack.filter((entry) => entry.isOpen());
}
